import { Box, Card, Stack, Typography } from "@mui/material";
import { usePageAccess } from "../auth/access";
import { PAGE_ACCESS } from "../nav/pageAccess";
import { Trans, useTranslation } from "react-i18next";
import type { PremiseConfig } from "@shared/types/foyer";
import { PageContainer, PageHeader } from "../components/PageHeader";
import DemoPaymentsBanner from "../components/offers/DemoPaymentsBanner";
import GuestRequestQueue from "../components/offers/GuestRequestQueue";
import { tokens } from "../theme";

/**
 * Requests — the staff inbox (`POS-9`).
 *
 * ## Why this is its own section and not a panel on Offers
 *
 * Authoring a catalogue and working a queue are two jobs done by two people at
 * two times of day. `canEditLayout` writes the menu; `canHandleGuestRequests`
 * takes the money — the permissions were split by POS-6 for exactly that
 * reason, and a reception desk that has to scroll past a spa package editor to
 * find the order it is meant to deliver is a desk that stops looking. So it
 * hangs beside Offers in `FRONTLAYER_SECTIONS`, at `/frontlayer/requests`,
 * carrying its own permission — which is what makes the section DISAPPEAR for a
 * user who does not hold it rather than open onto a refusal.
 *
 * ## Two layers, one page
 *
 * With a location scoped it is that hotel's queue. With a customer scoped and
 * NO location it is every one of its hotels, stacked and named — the chain
 * shape `CHAIN_CAPABLE_SECTIONS` exists for, and the reason `"requests"` joins
 * `"offers"` in that set. A duty manager covering four properties should not
 * have to change the header scope four times to find out whether anybody is
 * waiting.
 *
 * Care premises are excluded at both layers: offers are hotel-only for v1
 * (§10), and care already has `CareTask` and `ServiceRequestQueue`, which are
 * PHI-shaped and must never be merged with a commercial queue.
 */

interface Props {
  /** Exactly one of the two. A location, or the whole customer. */
  premise?: PremiseConfig;
  companyName?: string;
  /** The customer's hotels, when there is no location scoped. */
  chainPremises?: { premiseId: string; premiseName: string }[];
}

export default function RequestsPage({
  premise,
  companyName,
  chainPremises,
}: Props) {
  const canHandle = usePageAccess()(PAGE_ACCESS.guestRequests);
  const { t } = useTranslation("offers");

  if (!canHandle) return <NotYourQueue />;

  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("requestsPage.eyebrow")}
        title={t("requestsPage.title")}
        subtitle={
          premise
            ? t("requestsPage.subtitlePremise")
            : t("requestsPage.subtitleChain", {
                company: companyName ?? t("requestsPage.thisCustomer"),
              })
        }
      />

      {/*
       * §6 requires the "Demo payments" banner on every surface where the
       * always-accepts provider is active, and the request queue is one of
       * them — this is the screen where somebody presses Accept and books a
       * charge, so it is the last place the demo provider should be invisible.
       * The component is POS-8's and asks the server itself; there is
       * deliberately no second banner.
       */}
      <DemoPaymentsBanner />

      {premise ? (
        <GuestRequestQueue
          premiseId={premise.premiseId}
          rooms={premise.rooms}
          defaultLocale={premise.defaultLocale}
        />
      ) : (
        <ChainQueues premises={chainPremises ?? []} />
      )}
    </PageContainer>
  );
}

/**
 * Every hotel of the customer, each with its own queue and its own name above
 * it.
 *
 * One list per premise rather than one merged list with a hotel column: the
 * verbs are premise-scoped on the server, the waiting counts mean nothing added
 * together, and a duty manager reads "Hasselbacken has three waiting" far
 * faster than they scan a mixed list for the rows that are theirs.
 */
function ChainQueues({
  premises,
}: {
  premises: { premiseId: string; premiseName: string }[];
}) {
  const { t } = useTranslation("offers");
  if (premises.length === 0) {
    return (
      <Card variant="outlined" sx={{ p: 4 }}>
        <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
          {t("requestsPage.noHotels")}
        </Typography>
      </Card>
    );
  }
  return (
    <Stack spacing={4}>
      {premises.map((p) => (
        <Box key={p.premiseId}>
          <GuestRequestQueue premiseId={p.premiseId} title={p.premiseName} />
        </Box>
      ))}
    </Stack>
  );
}

/**
 * A direct link to a queue somebody may not work.
 *
 * The nav already hides the section, so this is only ever reached by a
 * bookmark or a pasted URL — and it says which right is missing rather than
 * showing an empty table, because an empty queue and a forbidden one look
 * identical and only one of them is worth telling somebody about.
 */
function NotYourQueue() {
  const { t } = useTranslation("offers");
  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("requestsPage.eyebrow")}
        title={t("requestsPage.title")}
      />
      <Card variant="outlined" sx={{ p: 4 }}>
        <Stack spacing={1} alignItems="flex-start">
          <Typography sx={{ fontWeight: "fontWeightBold", color: tokens.ink }}>
            {t("requestsPage.notYourQueue")}
          </Typography>
          <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
            <Trans
              t={t}
              i18nKey="requestsPage.notYourQueueHelp"
              components={{ strong: <strong /> }}
            />
          </Typography>
        </Stack>
      </Card>
    </PageContainer>
  );
}
